/* eslint-disable no-inline-styles/no-inline-styles */
import { builder } from '@builder.io/sdk';
import GlobalLayout from 'layout';
import { GetServerSideProps } from 'next';

import ComponentGenerator from 'utils/builder/ComponentGenerator';
import { RenderBuilderComponents } from 'utils/builder/RenderBuilderContent';
import { AbAntiFlickerGate, GATE_CLASS, useStickyAbVariant } from 'utils/builder/StickyAbVariant';
import { fetchLayoutModels } from 'utils/builder/builder.sdk';
import derivePageChrome from 'utils/builder/derivePageChrome';
import { primeAboveFoldSwitchback } from 'utils/builder/primeAboveFoldSwitchback';
import { stripPageData } from 'utils/builder/stripPageData';
import fetchListingBlob, { isListingSlug } from 'utils/fetch/fetchListingBlob';

import type { FooterVariant, HeaderVariant, PageProps } from 'types/global';

const MODEL = 'page';

type RegularPageProps = PageProps & {
  headerVariant?: HeaderVariant;
  footerVariant?: FooterVariant;
};

const Page = ({ pageData, layoutData, preview, listingData, headerVariant, footerVariant }: RegularPageProps) => {
  // The A/B variant is resolved on the CLIENT (see StickyAbVariant): CloudFront
  // strips/mangles the sticky cookie and rotates the forwarded IP, so the server
  // cannot pick a stable-per-visitor variant. The server renders the parent and
  // ships every variation; this hook swaps in the visitor's sticky variant before
  // paint and publishes it to the store for Marketo attribution + form gating.
  const { activeContent, activeData } = useStickyAbVariant(pageData);

  // Chrome is derived from the ACTIVE variant so an immersive child variation
  // (e.g. Free Trial FTUX) gets the right nav/footer once the client resolves it.
  const { headerVariant: resolvedHeaderVariant, footerVariant: resolvedFooterVariant } = activeData
    ? derivePageChrome(activeData)
    : { headerVariant, footerVariant };

  if (!activeData) {
    return (
      <GlobalLayout
        preview={preview}
        seoData={activeData}
        headerVariant={resolvedHeaderVariant}
        footerVariant={resolvedFooterVariant}
        {...layoutData}
      >
        <div style={{ minHeight: '100vh' }} />
      </GlobalLayout>
    );
  }

  return (
    <GlobalLayout
      preview={preview}
      seoData={activeData}
      headerVariant={resolvedHeaderVariant}
      footerVariant={resolvedFooterVariant}
      {...layoutData}
    >
      <AbAntiFlickerGate pageData={pageData} />
      <ComponentGenerator>
        <div className={GATE_CLASS}>
          <RenderBuilderComponents
            model={MODEL}
            content={activeContent}
            data={{ listingData: JSON.stringify(listingData) }}
          />
        </div>
      </ComponentGenerator>
    </GlobalLayout>
  );
};

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY || '');

export const getServerSideProps: GetServerSideProps = async ({ params, res, query, preview: draftPreview }) => {
  const slug = (params?.slug as string[])?.join('/');
  const preview = process.env.BUILDER === '1';
  // BUILDER=1 is also set on the production Vercel env, so it cannot alone
  // distinguish a real visitor from someone editing in Builder.io. The editor
  // iframe always loads URLs with `?builder.space=...` (and usually
  // `?builder.preview=...`), so use those query params to decide whether
  // missing content should fall through or 404.
  const isBuilderEditor = !!query['builder.space'] || !!query['builder.preview'];
  const urlPath = '/' + (slug || '');

  // The SDK short-circuits A/B selection on the server, so the fetch returns the
  // full content INCLUDING its `variations`. The variant is chosen on the CLIENT
  // (see StickyAbVariant) because CloudFront prevents any stable per-visitor
  // signal at the origin, so for A/B pages we KEEP `variations` in the payload for
  // the browser to pick from (stripPageData keepVariations below); non-A/B pages
  // still drop it. We intentionally do NOT pass a `fields` filter here: filtering
  // to specific fields returns the variations' data in a shape that fails to
  // parse, so fetch the whole content like the client SDK does.
  //
  // includeRefs: true inlines referenced content (forms, etc.) into pageData
  // so components like LandingPageHero/ConversionPanel/FreeTrialFTUX read
  // form.value.data from SSR instead of firing per-visit client-side
  // useBuilderReference fetches against cdn.builder.io.
  // The three fetches are independent; doing them sequentially added two
  // full upstream round trips to TTFB on every edge-cache miss.
  const [pageData, layoutData, listingData] = await Promise.all([
    builder
      .get(MODEL, {
        userAttributes: { urlPath },
        prerender: false,
        options: {
          noTraverse: !preview,
          includeRefs: true,
        },
        // Only serve unpublished drafts to the Builder.io editor iframe. BUILDER=1
        // is set on the production env too, so `preview` is true for real visitors
        // and keying includeUnpublished on it leaked never-published (and
        // un-published) pages at their real URL. isBuilderEditor is the query-param
        // signal that actually distinguishes an editing session from a visitor.
        includeUnpublished: isBuilderEditor,
      })
      .toPromise(),
    // Next.js Preview Mode (context.preview, cookie-gated via
    // /api/draft-preview) swaps the chrome for its Builder drafts. Page content
    // keeps its own isBuilderEditor gate above; this only affects the chrome.
    fetchLayoutModels(preview, !!draftPreview),
    fetchListingBlob(slug),
  ]);

  // builder.get() can resolve to an empty object (not null) when no content
  // matches the urlPath, so check for the actual page payload to trigger 404.
  // Skip 404 only when the request is from the Builder.io editor iframe so
  // editors can author new pages that have no published content yet.
  if (!pageData?.data && !isBuilderEditor) {
    return { notFound: true };
  }

  // A/B test present when the parent testRatio is < 1 (the remaining share is
  // split across variations). The variant itself is chosen on the client.
  const testRatio = (pageData as { testRatio?: number } | null | undefined)?.testRatio;
  const isAbTestActive = typeof testRatio === 'number' && testRatio < 1;

  // The server always renders the parent; the client swaps in the visitor's
  // sticky variant. Chrome is derived from the parent for SSR and re-derived on
  // the client from the resolved variant.
  const resolvedContent = pageData;
  const { headerVariant, footerVariant } = derivePageChrome(resolvedContent?.data);

  // Cache header strategy: see lp/[slug].tsx for the full rationale. Browsers and
  // the CloudFront layer never cache (Cache-Control: private, no-cache). The
  // Vercel edge may cache non-A/B pages via CDN-Cache-Control, but A/B pages must
  // NOT be edge-cached now that the variant is chosen per request on the server:
  // a cached variant (and its Set-Cookie) would be replayed to every visitor in
  // the window and freeze the test.
  // no-store dropped to keep pages bfcache-eligible; see lp/[slug].tsx.
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  // Draft-preview responses carry unpublished chrome, so they must never seed
  // the shared Vercel edge cache (same reason as the Builder editor gate).
  if (!isBuilderEditor && !draftPreview) {
    // A/B pages now render the parent for every visitor and resolve the variant on
    // the client, so the HTML is identical per request and can be edge-cached like
    // any other page. (It was previously `no-store` to avoid replaying a
    // server-picked variant, which no longer applies.)
    //
    // Listing pages (blog, press, etc.) render a build-time blob snapshot and sort
    // it by lastUpdated. The default 24h stale-while-revalidate lets edge POPs keep
    // serving an outdated ordering long after content changes, so a freshly revised
    // post can be missing from page 1 intermittently. Cap the stale window on those
    // routes so the ordering converges within minutes.
    const staleWhileRevalidate = isListingSlug(slug) ? 300 : 86400;
    res.setHeader('CDN-Cache-Control', `public, s-maxage=300, stale-while-revalidate=${staleWhileRevalidate}`);
  }

  // Defense in depth for noindex pages: some crawlers honor the X-Robots-Tag
  // response header but not the <meta name="robots"> tag in layout/Head. Mirror
  // that tag from the same data.noIndex Builder field, from the resolved variation.
  if (resolvedContent?.data?.noIndex) {
    res.setHeader('X-Robots-Tag', 'noindex, nofollow');
  }

  return {
    props: {
      // Prime the first above-the-fold Switchback as the LCP element, but only
      // on pages without a hero image (the guard lives in the util). Runs on the
      // stripped clone so it never mutates the SDK's cached fetch result. A/B
      // pages KEEP `variations` (keepVariations) so the client can render the
      // visitor's sticky variant; non-A/B pages drop it to stay slim.
      pageData: primeAboveFoldSwitchback(stripPageData(resolvedContent, { keepVariations: isAbTestActive })) || {},
      // Also strip Builder editor metadata from header/footer/announcementBar
      // payloads. They get serialized into __NEXT_DATA__ on every page so
      // dropping their meta/screenshot/createdBy etc. is a per-route saving.
      layoutData: stripPageData(layoutData) || {},
      preview: preview || false,
      listingData: listingData || [],
      headerVariant,
      footerVariant,
    },
  };
};

export default Page;
